import type { Node } from '@xyflow/react';

export interface GuideLines {
  horizontal: number[];
  vertical: number[];
}

export interface SnapResult {
  dx: number;
  dy: number;
  guides: GuideLines;
}

const THRESHOLD = 10;

interface BBox {
  left: number;
  centerX: number;
  right: number;
  top: number;
  centerY: number;
  bottom: number;
}

function getBBox(node: Node): BBox | null {
  const x = node.position.x;
  const y = node.position.y;
  const w = (node.style?.width as number) ?? node.measured?.width ?? 0;
  const h = (node.style?.height as number) ?? node.measured?.height ?? 0;
  if (!w || !h) return null;
  return {
    left: x,
    centerX: x + w / 2,
    right: x + w,
    top: y,
    centerY: y + h / 2,
    bottom: y + h,
  };
}

export function computeSnapAndGuides(
  draggedNode: Node,
  allNodes: Node[]
): SnapResult {
  const dragged = getBBox(draggedNode);
  if (!dragged) return { dx: 0, dy: 0, guides: { horizontal: [], vertical: [] } };

  const others = allNodes
    .filter((n) => n.id !== draggedNode.id && n.type !== 'placeholderNode')
    .map(getBBox)
    .filter(Boolean) as BBox[];

  const draggedXPoints = [dragged.left, dragged.centerX, dragged.right];
  const draggedYPoints = [dragged.top, dragged.centerY, dragged.bottom];

  let bestDx = 0;
  let bestDxDist = THRESHOLD + 1;
  let bestDy = 0;
  let bestDyDist = THRESHOLD + 1;

  const verticalGuides = new Set<number>();
  const horizontalGuides = new Set<number>();

  for (const other of others) {
    const otherXPoints = [other.left, other.centerX, other.right];
    const otherYPoints = [other.top, other.centerY, other.bottom];

    for (const dx_src of draggedXPoints) {
      for (const ox of otherXPoints) {
        const dist = Math.abs(dx_src - ox);
        if (dist <= THRESHOLD) {
          if (dist < bestDxDist) {
            bestDxDist = dist;
            bestDx = ox - dx_src;
          }
        }
      }
    }

    for (const dy_src of draggedYPoints) {
      for (const oy of otherYPoints) {
        const dist = Math.abs(dy_src - oy);
        if (dist <= THRESHOLD) {
          if (dist < bestDyDist) {
            bestDyDist = dist;
            bestDy = oy - dy_src;
          }
        }
      }
    }
  }

  const snappedDragged: BBox = {
    left: dragged.left + bestDx,
    centerX: dragged.centerX + bestDx,
    right: dragged.right + bestDx,
    top: dragged.top + bestDy,
    centerY: dragged.centerY + bestDy,
    bottom: dragged.bottom + bestDy,
  };

  for (const other of others) {
    const otherXPoints = [other.left, other.centerX, other.right];
    const otherYPoints = [other.top, other.centerY, other.bottom];

    const snappedXPoints = [snappedDragged.left, snappedDragged.centerX, snappedDragged.right];
    const snappedYPoints = [snappedDragged.top, snappedDragged.centerY, snappedDragged.bottom];

    for (const sx of snappedXPoints) {
      for (const ox of otherXPoints) {
        if (Math.abs(sx - ox) < 1) {
          verticalGuides.add(ox);
        }
      }
    }

    for (const sy of snappedYPoints) {
      for (const oy of otherYPoints) {
        if (Math.abs(sy - oy) < 1) {
          horizontalGuides.add(oy);
        }
      }
    }
  }

  return {
    dx: bestDxDist <= THRESHOLD ? bestDx : 0,
    dy: bestDyDist <= THRESHOLD ? bestDy : 0,
    guides: {
      horizontal: Array.from(horizontalGuides),
      vertical: Array.from(verticalGuides),
    },
  };
}
